import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DndContext, DragEndEvent, PointerSensor, KeyboardSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { motion } from 'framer-motion';
import { BeakerIcon, MagnifyingGlassIcon, PlusIcon, TruckIcon, CalendarDaysIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Sample, SampleStatus, samplesAPI } from '../../services/api';
import { Button, Modal, Spinner } from '../ui';

type ColumnId = 'REQUESTED' | 'PREPARING' | 'SHIPPED' | 'DELIVERED' | 'REVIEW' | 'CLOSED';

const columns: Array<{ id: ColumnId; title: string; statuses: SampleStatus[]; color: string }> = [
  { id: 'REQUESTED', title: 'Requested', statuses: ['REQUESTED'], color: 'bg-violet-500' },
  { id: 'PREPARING', title: 'Preparing', statuses: ['PENDING'], color: 'bg-amber-500' },
  { id: 'SHIPPED', title: 'Shipped', statuses: ['SHIPPED'], color: 'bg-blue-500' },
  { id: 'DELIVERED', title: 'Delivered', statuses: ['DELIVERED'], color: 'bg-cyan-500' },
  { id: 'REVIEW', title: 'Review', statuses: ['UNDER_REVIEW'], color: 'bg-fuchsia-500' },
  { id: 'CLOSED', title: 'Decision / Closed', statuses: ['APPROVED', 'REJECTED', 'RETURNED', 'LOST', 'EXPIRED'], color: 'bg-emerald-500' },
];

const statusColumn = (status: SampleStatus) => columns.find((column) => column.statuses.includes(status))?.id || 'CLOSED';

const SampleCard: React.FC<{ sample: Sample; basePath: string; onAdvance: (sample: Sample) => void }> = ({ sample, basePath, onAdvance }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: `sample-${sample.id}`, data: { sample } });
  return (
    <motion.article
      ref={setNodeRef}
      layout
      style={{ transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.55 : 1 }}
      className="rounded-2xl border border-white/70 bg-white p-4 shadow-sm transition-shadow hover:shadow-lg"
    >
      <button {...listeners} {...attributes} className="w-full cursor-grab text-left active:cursor-grabbing" aria-label={`Move ${sample.productName}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-semibold text-neutral-900">{sample.productName}</p>
            <p className="mt-0.5 text-xs text-neutral-500">{sample.client?.name || 'Client'} · {sample.quantity} {sample.unit}</p>
          </div>
          <span className="rounded-full bg-neutral-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-neutral-600">
            {sample.status.replaceAll('_', ' ')}
          </span>
        </div>
        {(sample.trackingNumber || sample.lotNumber) && (
          <div className="mt-3 rounded-xl bg-neutral-50 p-2.5 text-xs text-neutral-600">
            {sample.lotNumber && <p>Lot: <span className="font-semibold text-neutral-800">{sample.lotNumber}</span></p>}
            {sample.trackingNumber && <p className="truncate">Tracking: <span className="font-mono text-neutral-800">{sample.trackingNumber}</span></p>}
            {sample.shippingCarrier && <p>Carrier: {sample.shippingCarrier}</p>}
          </div>
        )}
      </button>
      <div className="mt-3 flex items-center justify-between border-t border-neutral-100 pt-3">
        <Link to={`${basePath}/samples/${sample.id}`} className="text-xs font-semibold text-primary-600 hover:text-primary-700">View details</Link>
        {!['APPROVED', 'REJECTED', 'RETURNED', 'LOST', 'EXPIRED'].includes(sample.status) && (
          <button onClick={() => onAdvance(sample)} className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-600 hover:text-neutral-900">
            Next <ArrowRightIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </motion.article>
  );
};

const PipelineColumn: React.FC<{
  column: typeof columns[number];
  samples: Sample[];
  basePath: string;
  onAdvance: (sample: Sample) => void;
}> = ({ column, samples, basePath, onAdvance }) => {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  return (
    <section ref={setNodeRef} className={`min-h-[24rem] w-[19rem] shrink-0 rounded-3xl border p-3 transition-colors ${isOver ? 'border-primary-400 bg-primary-50' : 'border-neutral-200 bg-neutral-100/70'}`}>
      <div className="mb-3 flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${column.color}`} />
          <h2 className="text-sm font-bold text-neutral-800">{column.title}</h2>
        </div>
        <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-neutral-500 shadow-sm">{samples.length}</span>
      </div>
      <div className="space-y-3">
        {samples.map((sample) => <SampleCard key={sample.id} sample={sample} basePath={basePath} onAdvance={onAdvance} />)}
        {samples.length === 0 && <div className="rounded-2xl border border-dashed border-neutral-300 p-8 text-center text-xs text-neutral-400">Drop samples here</div>}
      </div>
    </section>
  );
};

const SamplesPipeline: React.FC = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const basePath = isAdmin ? '/admin' : '';
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Sample | null>(null);
  const [modal, setModal] = useState<'ship' | 'decision' | null>(null);
  const [shipForm, setShipForm] = useState({ trackingNumber: '', lotNumber: '', shippingCarrier: '', shippingMethod: '', estimatedDeliveryDate: '' });
  const [decision, setDecision] = useState<SampleStatus>('APPROVED');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const results = await Promise.all(columns.map((column) => samplesAPI.getAll({ statuses: column.statuses, limit: 100, search: search || undefined })));
      setSamples(results.flatMap((result) => result.data));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not load the sample pipeline');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { const timer = window.setTimeout(load, 250); return () => window.clearTimeout(timer); }, [load]);

  const grouped = useMemo(() => Object.fromEntries(columns.map((column) => [column.id, samples.filter((sample) => column.statuses.includes(sample.status))])) as Record<ColumnId, Sample[]>, [samples]);

  const openShip = (sample: Sample) => {
    setSelected(sample);
    setShipForm({ trackingNumber: sample.trackingNumber || '', lotNumber: sample.lotNumber || '', shippingCarrier: sample.shippingCarrier || '', shippingMethod: sample.shippingMethod || '', estimatedDeliveryDate: sample.estimatedDeliveryDate?.split('T')[0] || '' });
    setModal('ship');
  };

  const move = async (sample: Sample, column: ColumnId) => {
    if (statusColumn(sample.status) === column) return;
    if (column === 'SHIPPED') return openShip(sample);
    if (column === 'CLOSED') { setSelected(sample); setDecision('APPROVED'); setModal('decision'); return; }
    const status: Record<Exclude<ColumnId, 'SHIPPED' | 'CLOSED'>, SampleStatus> = { REQUESTED: 'REQUESTED', PREPARING: 'PENDING', DELIVERED: 'DELIVERED', REVIEW: 'UNDER_REVIEW' };
    setSaving(true);
    try { await samplesAPI.updateStatus(sample.id, { status: status[column as keyof typeof status] }); await load(); }
    catch (err: any) { setError(err.response?.data?.message || 'Could not move sample'); }
    finally { setSaving(false); }
  };

  const advance = (sample: Sample) => {
    const next: Partial<Record<SampleStatus, ColumnId>> = { REQUESTED: 'PREPARING', PENDING: 'SHIPPED', SHIPPED: 'DELIVERED', DELIVERED: 'REVIEW', UNDER_REVIEW: 'CLOSED' };
    const target = next[sample.status];
    if (target) move(sample, target);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    const sample = active.data.current?.sample as Sample | undefined;
    if (sample && over) move(sample, over.id as ColumnId);
  };

  const submitShip = async () => {
    if (!selected || !shipForm.trackingNumber.trim() || !shipForm.lotNumber.trim() || !shipForm.shippingCarrier.trim()) return;
    setSaving(true);
    try { await samplesAPI.ship(selected.id, { ...shipForm, estimatedDeliveryDate: shipForm.estimatedDeliveryDate || undefined }); setModal(null); await load(); }
    catch (err: any) { setError(err.response?.data?.message || 'Could not ship sample'); }
    finally { setSaving(false); }
  };

  const submitDecision = async () => {
    if (!selected) return;
    setSaving(true);
    try { await samplesAPI.updateStatus(selected.id, { status: decision }); setModal(null); await load(); }
    catch (err: any) { setError(err.response?.data?.message || 'Could not close sample'); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-full bg-gradient-to-br from-slate-50 via-white to-violet-50 p-4 sm:p-6 lg:p-8">
      <header className="mb-6 rounded-3xl bg-gradient-to-r from-violet-700 via-indigo-700 to-blue-700 p-6 text-white shadow-xl">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="rounded-2xl bg-white/15 p-3"><BeakerIcon className="h-8 w-8" /></div>
            <div><h1 className="text-2xl font-black text-white sm:text-3xl">Samples Pipeline</h1><p className="mt-1 text-sm text-indigo-100">Request, prepare, ship, review, and convert with every detail in one place.</p></div>
          </div>
          <Link to={`${basePath}/samples/new`}><Button variant="secondary" leftIcon={<PlusIcon />}>Request sample</Button></Link>
        </div>
        <div className="relative mt-5 max-w-xl">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-indigo-200" />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search product, client, lot, or tracking…" className="w-full rounded-2xl border border-white/20 bg-white/10 py-3 pl-10 pr-4 text-sm text-white placeholder:text-indigo-200 focus:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/50" />
        </div>
      </header>
      {error && <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading ? <div className="flex min-h-[24rem] items-center justify-center"><Spinner size="lg" /></div> : (
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-5">{columns.map((column) => <PipelineColumn key={column.id} column={column} samples={grouped[column.id]} basePath={basePath} onAdvance={advance} />)}</div>
        </DndContext>
      )}
      {saving && <div className="fixed bottom-5 right-5 flex items-center gap-2 rounded-full bg-neutral-900 px-4 py-2 text-sm text-white shadow-xl"><Spinner size="sm" /> Saving…</div>}

      <Modal isOpen={modal === 'ship'} onClose={() => setModal(null)} title="Ship sample" description="Lot, carrier, and tracking are required when the sample leaves." footer={<><Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button><Button onClick={submitShip} isLoading={saving} leftIcon={<TruckIcon />}>Mark shipped</Button></>}>
        <div className="grid gap-4 p-1 sm:grid-cols-2">
          {[['trackingNumber', 'Tracking number'], ['lotNumber', 'Lot number'], ['shippingCarrier', 'Carrier'], ['shippingMethod', 'Shipping method']].map(([name, label]) => <label key={name} className="text-sm font-semibold text-neutral-700">{label}{name !== 'shippingMethod' && ' *'}<input value={(shipForm as any)[name]} onChange={(event) => setShipForm((current) => ({ ...current, [name]: event.target.value }))} className="mt-1.5 w-full rounded-xl border border-neutral-300 px-3 py-2.5 font-normal focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100" /></label>)}
          <label className="text-sm font-semibold text-neutral-700 sm:col-span-2"><CalendarDaysIcon className="mr-1 inline h-4 w-4" />Estimated delivery<input type="date" value={shipForm.estimatedDeliveryDate} onChange={(event) => setShipForm((current) => ({ ...current, estimatedDeliveryDate: event.target.value }))} className="mt-1.5 w-full rounded-xl border border-neutral-300 px-3 py-2.5 font-normal" /></label>
        </div>
      </Modal>

      <Modal isOpen={modal === 'decision'} onClose={() => setModal(null)} title="Close sample" description="Choose the final outcome for this sample." footer={<><Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button><Button onClick={submitDecision} isLoading={saving}>Save outcome</Button></>}>
        <select value={decision} onChange={(event) => setDecision(event.target.value as SampleStatus)} className="w-full rounded-xl border border-neutral-300 px-3 py-3">
          <option value="APPROVED">Approved</option><option value="REJECTED">Rejected</option><option value="RETURNED">Returned</option><option value="LOST">Lost</option><option value="EXPIRED">Expired</option>
        </select>
      </Modal>
    </div>
  );
};

export default SamplesPipeline;
